import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockSystemParametersData } from '@/pages/system-parameters/mocks/system-parameters.mock';

export type NotificationType = 'warning' | 'info' | 'error' | 'success';

export interface SystemReminder {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: Date;
  isRead: boolean;
}

interface NotificationContextType {
  reminders: SystemReminder[];
  addReminder: (reminder: Omit<SystemReminder, 'id' | 'createdAt' | 'isRead'>) => void;
  markAsRead: (id: string) => void;
  clearReminder: (id: string) => void;
  clearAllReminders: () => void;
  refreshReminders: () => void;
  unreadCount: number;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<SystemReminder[]>([]);
  const isRefreshingRef = useRef(false);
  const hasInitialized = useRef(false);

  // Cargar recordatorios al inicializar (solo una vez)
  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;
    
    const systemReminders = mockSystemParametersData.reminders.items
      .filter(reminder => reminder.isActive && reminder.additionalData.isEnabled)
      .map(reminder => ({
        id: reminder.id,
        type: 'warning' as NotificationType,
        title: `${reminder.additionalData.action} - ${reminder.additionalData.time}`,
        message: reminder.additionalData.description,
        action: {
          label: reminder.additionalData.clickAction,
          onClick: () => navigate('/merchandise-movements?action=new-exit')
        },
        createdAt: new Date(),
        isRead: false
      }));
    
    setReminders(systemReminders);
  }, [navigate]); // Keep navigate as dependency but prevent multiple executions with hasInitialized

  // Función para refrescar recordatorios (para uso manual)
  const refreshReminders = useCallback(() => {
    // Prevent concurrent executions
    if (isRefreshingRef.current) {
      return;
    }
    
    isRefreshingRef.current = true;
    
    const systemReminders = mockSystemParametersData.reminders.items
      .filter(reminder => reminder.isActive && reminder.additionalData.isEnabled)
      .map(reminder => ({
        id: reminder.id,
        type: 'warning' as NotificationType,
        title: `${reminder.additionalData.action} - ${reminder.additionalData.time}`,
        message: reminder.additionalData.description,
        action: {
          label: reminder.additionalData.clickAction,
          onClick: () => navigate('/merchandise-movements?action=new-exit')
        },
        createdAt: new Date(),
        isRead: false
      }));
    
    // Solo actualizar si realmente hay cambios
    setReminders(prev => {
      // Verificar si los recordatorios son los mismos (comparar solo datos, no funciones)
      if (prev.length === systemReminders.length) {
        const areSame = prev.every((prevReminder, index) => 
          prevReminder.id === systemReminders[index]?.id &&
          prevReminder.title === systemReminders[index]?.title &&
          prevReminder.message === systemReminders[index]?.message
        );
        if (areSame) {
          isRefreshingRef.current = false;
          return prev; // No cambiar si son iguales
        }
      }
      
      isRefreshingRef.current = false;
      return systemReminders;
    });
  }, [navigate]);

  const addReminder = useCallback((reminder: Omit<SystemReminder, 'id' | 'createdAt' | 'isRead'>) => {
    const newReminder: SystemReminder = {
      ...reminder,
      id: Math.random().toString(36).substr(2, 9),
      createdAt: new Date(),
      isRead: false,
    };
    setReminders(prev => [newReminder, ...prev]);
  }, []);

  const markAsRead = useCallback((id: string) => {
    setReminders(prev => prev.map(reminder => 
      reminder.id === id ? { ...reminder, isRead: true } : reminder
    ));
  }, []);

  const clearReminder = useCallback((id: string) => {
    setReminders(prev => prev.filter(reminder => reminder.id !== id));
  }, []);

  const clearAllReminders = useCallback(() => {
    setReminders([]);
  }, []);

  const unreadCount = reminders.filter(reminder => !reminder.isRead).length;

  const value: NotificationContextType = {
    reminders,
    addReminder,
    markAsRead,
    clearReminder,
    clearAllReminders,
    refreshReminders,
    unreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};